import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  getWithdrawalBalanceSummary,
  getWithdrawalEligibility,
  type WithdrawalBalanceSummary,
  type WithdrawalEligibility,
} from "@/lib/api";
import { formatAmount } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { RefreshCw, Send, Wallet, AlertTriangle, Loader2 } from "lucide-react";

const METHOD_LABELS: Record<string, string> = {
  airtel: "Airtel Money",
  orange: "Orange Money",
  mpesa: "M-Pesa (Vodacom)",
  afrimoney: "Afrimoney",
};

const METHOD_TONES: Record<string, string> = {
  airtel: "border-red-200 bg-red-50",
  orange: "border-orange-200 bg-orange-50",
  mpesa: "border-emerald-200 bg-emerald-50",
  afrimoney: "border-amber-200 bg-amber-50",
};

export function WalletBalancesPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<WithdrawalBalanceSummary | null>(null);
  const [eligibility, setEligibility] = useState<WithdrawalEligibility | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const [s, e] = await Promise.all([
        getWithdrawalBalanceSummary(),
        getWithdrawalEligibility(),
      ]);
      setSummary(s);
      setEligibility(e);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur de chargement");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currencies = useMemo(() => (summary ? Object.keys(summary) : []), [summary]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Portefeuille"
        subtitle="Vos soldes disponibles pour retrait, par méthode et devise."
        actions={
          <div className="flex gap-2">
            <button
              onClick={() => load(true)}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
            >
              {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Rafraîchir
            </button>
            {eligibility?.has_withdrawal_access && (
              <button
                onClick={() => navigate("/finance/withdrawal")}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold bg-[#181c32] text-white rounded hover:bg-[#0f1220]"
              >
                <Send size={14} />
                Initier un retrait
              </button>
            )}
          </div>
        }
      />

      {!eligibility?.has_withdrawal_access && !loading && (
        <div className="rounded border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-amber-900">Fonctionnalité retrait non activée</div>
            <div className="text-sm text-amber-800 mt-0.5">
              Vous pouvez consulter vos soldes mais pas encore initier de retrait depuis ce dashboard.
              Contactez le support pour activer <code className="font-mono text-xs">MOBILE_MONEY_WITHDRAWAL</code>.
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-[#7e8299]">
          <Loader2 className="inline animate-spin mr-2" /> Chargement des soldes…
        </div>
      ) : error ? (
        <div className="rounded border border-red-200 bg-red-50 p-4 text-red-800">Erreur : {error}</div>
      ) : !summary || currencies.length === 0 ? (
        <div className="rounded border border-gray-200 p-8 text-center text-[#7e8299]">
          Aucun solde à afficher.
        </div>
      ) : (
        currencies.map((cur) => {
          const currencyData = summary[cur];
          const total = Number(currencyData.total_balance);
          return (
            <section key={cur} className="bg-white rounded-lg border border-gray-200 shadow-sm">
              <header className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-[#181c32]">Solde en {cur}</h2>
                  <p className="text-sm text-[#7e8299]">Total disponible : <span className="font-mono font-semibold text-[#181c32]">{formatAmount(Math.round(total * 100), cur)}</span></p>
                </div>
                <Wallet size={22} className="text-[#7e8299]" />
              </header>
              <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {currencyData.operator_details.map((op) => {
                  const avail = Number(op.available_for_withdrawal);
                  const reserved = Number(op.reserved_withdrawal);
                  const tone = METHOD_TONES[op.method] ?? "border-gray-200 bg-gray-50";
                  return (
                    <div key={op.method} className={`rounded border ${tone} p-4`}>
                      <div className="text-xs font-semibold uppercase tracking-wide text-[#3f4254] opacity-70">
                        {METHOD_LABELS[op.method] ?? op.method}
                      </div>
                      <div className="mt-1 text-lg font-bold font-mono text-[#181c32]">
                        {formatAmount(Math.round(avail * 100), cur).replace(` ${cur}`, "")}
                      </div>
                      {reserved > 0 && (
                        <div className="mt-1 text-[11px] text-[#7e8299]">
                          Réservé : {formatAmount(Math.round(reserved * 100), cur).replace(` ${cur}`, "")}
                        </div>
                      )}
                      {eligibility?.has_withdrawal_access && avail > 0 && (
                        <button
                          onClick={() => navigate(`/finance/withdrawal?method=${op.method}&currency=${cur}`)}
                          className="mt-2 text-xs font-medium text-[#181c32] hover:underline"
                        >
                          Retirer →
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })
      )}

      <div className="text-xs text-[#7e8299] text-center">
        Actualisation manuelle. Les soldes sont mis en cache ~5 s côté serveur pour lisser les pics d'appel.
      </div>
    </div>
  );
}
